import { useState } from 'react';
import { collection, doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { Button, Modal, FormField } from './ui';
import { fmtMoney } from '../utils/currency';
import { buildEntry, postEntry, entryId, linesPurchaseExpense, linesSaleExpense } from '../utils/ledger';
import type { BankAccount, PurchaseExpense, SaleExpense } from '../types';

// ── Pay an unpaid Purchase / Sales Expense bill ──────────────────────────────
// Both kinds can be saved "not paid yet" (credit → Accrued Expenses). This
// settles one later: marks the doc paid, writes the bank transaction, moves
// the account balance and overwrites the bill's own journal entry so its
// credit moves from Accrued Expenses to the bank — same shape as
// Expenses.tsx's PayModal for operating bills. Shared by
// PurchaseExpenses.tsx and SalesExpenses.tsx.

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

export type ExpenseBill =
  | { kind: 'purchase'; expense: PurchaseExpense }
  | { kind: 'sale'; expense: SaleExpense };

export default function PayExpenseBillModal({ bill, accounts, onClose }: {
  bill: ExpenseBill; accounts: BankAccount[]; onClose: () => void;
}) {
  const { user, business } = useAuth();
  const e = bill.expense;
  const [bankAccountId, setBankAccountId] = useState((accounts.find(a => a.isDefault) ?? accounts[0])?.id ?? '');
  const [paidDate, setPaidDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const acct = accounts.find(a => a.id === bankAccountId) ?? null;

  async function pay() {
    if (!acct) return setError('Choose an account.');
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }
    const coll = bill.kind === 'purchase' ? 'purchaseExpenses' : 'saleExpenses';
    const sourceType = bill.kind === 'purchase' ? 'purchase_expense' : 'sale_expense';
    const amt = e.amountUSD;
    try {
      const expRef = doc(db, coll, e.id);
      const accRef = doc(db, 'bankAccounts', acct.id);
      const txRef  = doc(collection(db, 'bankTransactions'));
      await runTransaction(db, async tx => {
        const [expSnap, accSnap] = await Promise.all([tx.get(expRef), tx.get(accRef)]);
        if (!expSnap.exists()) throw new Error('This bill no longer exists.');
        if ((expSnap.data() as { paymentStatus?: string }).paymentStatus === 'paid') throw new Error('This bill is already paid.');
        if (!accSnap.exists()) throw new Error('That bank account no longer exists.');
        tx.update(expRef, stripUndef({
          paymentStatus: 'paid', paidUSD: amt,
          bankAccountId: acct.id, bankAccountName: acct.name,
          paidDate, bankTransactionId: txRef.id, updatedAt: serverTimestamp(),
        }));
        tx.set(txRef, stripUndef({
          businessId: business.id, accountId: acct.id, accountName: acct.name,
          currency: acct.currency, direction: 'out', amount: amt, date: paidDate,
          description: e.description, referenceType: sourceType, referenceId: e.id,
          category: e.category, createdAt: new Date().toISOString(), createdBy: user!.id,
        }));
        tx.update(accRef, { currentBalance: (accSnap.data() as BankAccount).currentBalance - amt });
        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType, sourceId: e.id, sourceCollection: coll,
          date: e.date, currency: e.currency, createdBy: user!.id,
          note: `${e.expenseNo} — ${e.description}`,
          lines: bill.kind === 'purchase'
            ? linesPurchaseExpense(amt, bill.expense.treatment, { bankAccountId: acct.id }, bill.expense.vendorEntityId)
            : linesSaleExpense(amt, !!bill.expense.chargeToCustomer, { bankAccountId: acct.id },
                bill.expense.customerEntityId, bill.expense.vendorEntityId),
        }), entryId(sourceType, e.id));
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the payment.');
    }
    setSaving(false);
  }

  return (
    <Modal open onClose={onClose} title="Pay Bill"
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={pay} loading={saving}>Mark Paid</Button>
      </>}>
      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}
      <div style={{ fontSize: 13, marginBottom: 14, color: 'var(--text-2)' }}>
        {e.expenseNo} · {e.description} — <strong>{fmtMoney(e.amountUSD, e.currency)}</strong>
      </div>
      <div className="form-row-2">
        <FormField label="Paid from" required>
          <select value={bankAccountId} onChange={ev => setBankAccountId(ev.target.value)}>
            <option value="">Select…</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name} — {fmtMoney(a.currentBalance, a.currency)}</option>)}
          </select>
        </FormField>
        <FormField label="Date" required>
          <input type="date" value={paidDate} max={todayISO()} onChange={ev => setPaidDate(ev.target.value)} />
        </FormField>
      </div>
      {acct && (
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
          Balance after: <span style={{ fontFamily: 'var(--font-num)' }}>{fmtMoney(acct.currentBalance - e.amountUSD, acct.currency)}</span>
        </div>
      )}
    </Modal>
  );
}
